/**
 * Innu Auth bootstrap — ported from Operations `shell/src/lib/auth.ts` with
 * the app slug changed to `recruiting`.
 *
 * Flow (INTEGRATION.md §7):
 *   1. On mount, read `shellToken` (+ optional `refreshToken`,
 *      `selectedCompanyId`, `path`) from the URL hash, store, scrub the hash.
 *   2. If the hash carried no token, fall back to localStorage.accessToken.
 *   3. If still no token OR the token is expired → POST /api/auth/refresh; if
 *      that fails too → window.location.href = INNU_SHELL_URL + '?app=recruiting'.
 *   4. Decode the JWT payload client-side for UI only (the API worker is the
 *      security boundary — it re-verifies every request via @innu/auth/core).
 *   5. Honor the per-app role override: if `appRoles.recruiting` exists, that
 *      is the effective role; the platform role is preserved.
 *
 * Token storage uses localStorage keys `accessToken` and `refreshToken`
 * (INTEGRATION.md §7.4 convention).
 */
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type InnuRole =
  | 'super_admin'
  | 'company_admin'
  | 'district_manager'
  | 'shop_manager'
  | 'service_advisor';

export interface InnuTokenPayload {
  sub: string;
  email: string;
  role: InnuRole;
  companyId: string;
  shopId: string | null;
  districtId: string | null;
  apps: string[];
  appRoles?: Record<string, InnuRole>;
  iat: number;
  exp: number;
}

export interface AuthenticatedUser extends InnuTokenPayload {
  platformRole: InnuRole;
  appContext: string | null;
}

const ROLE_RANK: Record<InnuRole, number> = {
  service_advisor: 1,
  shop_manager: 2,
  district_manager: 3,
  company_admin: 4,
  super_admin: 5,
};

export function hasMinRole(
  user: Pick<AuthenticatedUser, 'role'> | null | undefined,
  required: InnuRole,
): boolean {
  if (!user) return false;
  return (ROLE_RANK[user.role] ?? 0) >= ROLE_RANK[required];
}

export function hasAppAccess(
  user: Pick<AuthenticatedUser, 'apps'> | null | undefined,
  slug: string,
): boolean {
  return !!user?.apps?.includes(slug);
}

export const APP_SLUG = 'recruiting';
const LS_ACCESS = 'accessToken';
const LS_REFRESH = 'refreshToken';
const LS_COMPANY = 'recruiting.selectedCompanyId';

const INNU_SHELL_URL = import.meta.env.VITE_INNU_SHELL_URL ?? 'https://innu.app';

const INNU_CORE_URL =
  import.meta.env.VITE_INNU_CORE_URL ??
  'https://innu-core-api-112488489004.us-central1.run.app';

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4;
  return atob(pad ? padded + '='.repeat(4 - pad) : padded);
}

export function decodeJwtPayload(token: string): InnuTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    return JSON.parse(base64UrlDecode(parts[1])) as InnuTokenPayload;
  } catch {
    return null;
  }
}

function userFromPayload(payload: InnuTokenPayload): AuthenticatedUser {
  const override = payload.appRoles?.[APP_SLUG];
  const hasApp = payload.apps?.includes(APP_SLUG) ?? false;
  return {
    ...payload,
    platformRole: payload.role,
    role: hasApp && override ? override : payload.role,
    appContext: hasApp ? APP_SLUG : null,
  };
}

function isExpired(payload: InnuTokenPayload, skewSeconds = 30): boolean {
  return payload.exp <= Math.floor(Date.now() / 1000) + skewSeconds;
}

export interface ShellLaunchParams {
  access: string | null;
  refresh: string | null;
  /** super_admin company pick from the Shell (`selectedCompanyId=`). */
  selectedCompanyId: string | null;
  /** Inner route the Shell wants opened (`path=`), e.g. `/settings/stages`. */
  path: string | null;
}

/**
 * Reads the Shell launch params from the URL hash (query-string fallback for
 * older Shell builds) and scrubs them so they never reach logs or the back
 * button. Pure read — `consumeShellLaunch()` is the storing variant.
 */
export function readShellTokensFromUrl(): ShellLaunchParams {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const qs = new URLSearchParams(window.location.search);

  const access = hash.get('shellToken') ?? qs.get('shellToken') ?? qs.get('token') ?? null;
  const refresh = hash.get('refreshToken') ?? qs.get('refreshToken') ?? null;
  const selectedCompanyId =
    hash.get('selectedCompanyId') ?? qs.get('selectedCompanyId') ?? null;
  const rawPath = hash.get('path') ?? qs.get('path') ?? null;
  const path = rawPath && rawPath.startsWith('/') && !rawPath.startsWith('//') ? rawPath : null;

  if (access || refresh || selectedCompanyId || rawPath) {
    for (const key of ['shellToken', 'refreshToken', 'selectedCompanyId', 'path']) {
      hash.delete(key);
      qs.delete(key);
    }
    qs.delete('token');
    const newHash = hash.toString();
    const newSearch = qs.toString();
    history.replaceState(
      null,
      '',
      window.location.pathname +
        (newSearch ? `?${newSearch}` : '') +
        (newHash ? `#${newHash}` : ''),
    );
  }
  return { access, refresh, selectedCompanyId, path };
}

let pendingLaunchPath: string | null = null;

/**
 * The inner route the Shell asked for on launch, handed over exactly once.
 * `useShellRouteSync()` navigates there after the auth bootstrap resolves.
 */
export function consumeLaunchPath(): string | null {
  const p = pendingLaunchPath;
  pendingLaunchPath = null;
  return p;
}

/** Read + persist everything the Shell put in the URL. */
export function consumeShellLaunch(): ShellLaunchParams {
  const params = readShellTokensFromUrl();
  if (params.access) storeTokens(params.access, params.refresh);
  if (params.selectedCompanyId) storeSelectedCompanyId(params.selectedCompanyId);
  if (params.path) pendingLaunchPath = params.path;
  return params;
}

function storeTokens(access: string | null, refresh: string | null): void {
  try {
    if (access) localStorage.setItem(LS_ACCESS, access);
    if (refresh) localStorage.setItem(LS_REFRESH, refresh);
  } catch {
    // ignore (private mode)
  }
}

function clearTokens(): void {
  try {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_COMPANY);
  } catch {
    // ignore
  }
}

function storeSelectedCompanyId(id: string): void {
  try {
    localStorage.setItem(LS_COMPANY, id);
  } catch {
    // ignore
  }
}

export function getStoredAccessToken(): string | null {
  try {
    return localStorage.getItem(LS_ACCESS);
  } catch {
    return null;
  }
}

export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(LS_REFRESH);
  } catch {
    return null;
  }
}

/** Company the Shell selected for a super_admin (sent as X-Selected-Company-Id). */
export function getStoredSelectedCompanyId(): string | null {
  try {
    return localStorage.getItem(LS_COMPANY);
  } catch {
    return null;
  }
}

/**
 * POST {core}/api/auth/refresh — exchanges a refresh token for a new
 * access+refresh pair. Refresh tokens are single-use and rotated; the new
 * refresh token MUST be stored.
 *
 * SINGLE-FLIGHT: Innu refresh tokens are single-use, and REUSING a consumed
 * one revokes ALL of the user's tokens. Parallel requests that all see an
 * expired access token must therefore share one in-flight refresh.
 */
let refreshInFlight: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${INNU_CORE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      accessToken?: string;
      refreshToken?: string;
    };
    if (!data.accessToken) return null;
    storeTokens(data.accessToken, data.refreshToken ?? null);
    return data.accessToken;
  } catch {
    return null;
  }
}

export function shellLoginUrl(): string {
  return `${INNU_SHELL_URL}?app=${APP_SLUG}`;
}

function redirectToShell(): void {
  window.location.href = shellLoginUrl();
}

interface AuthContextValue {
  user: AuthenticatedUser | null;
  /** Always-current token getter (reads localStorage); preferred over a stale prop. */
  getToken: () => string | null;
  /** Trigger a refresh; returns the new access token or null on failure. */
  refresh: () => Promise<string | null>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type BootstrapState =
  | { phase: 'pending' }
  | { phase: 'ready'; user: AuthenticatedUser }
  | { phase: 'redirecting' };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BootstrapState>({ phase: 'pending' });

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      // Step 1 — pull tokens from the URL if the Shell launched us, persist
      consumeShellLaunch();

      // Step 2 — read whatever we now have
      let access = getStoredAccessToken();
      let payload = access ? decodeJwtPayload(access) : null;

      // Step 3 — if missing or expired, try refresh
      if (!payload || isExpired(payload)) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          access = refreshed;
          payload = decodeJwtPayload(refreshed);
        }
      }

      // Step 4 — still no valid token → redirect to the Shell
      if (!access || !payload) {
        clearTokens();
        if (!cancelled) setState({ phase: 'redirecting' });
        redirectToShell();
        return;
      }

      if (!cancelled) {
        setState({ phase: 'ready', user: userFromPayload(payload) });
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Proactive renewal: refresh ~2 min BEFORE expiry so requests never hit 401
  // at all — with the single-flight refresh above, a short Innu access-token
  // TTL is invisible to the user. Re-arms itself after every successful
  // refresh (new exp → new timer via state change).
  useEffect(() => {
    if (state.phase !== 'ready') return;

    const applyToken = (tok: string | null) => {
      if (!tok) return; // transient failure — per-request 401 retry still covers us
      const p = decodeJwtPayload(tok);
      if (p) setState({ phase: 'ready', user: userFromPayload(p) });
    };

    const msUntilRenew = state.user.exp * 1000 - Date.now() - 120_000;
    const timer = setTimeout(
      () => void refreshAccessToken().then(applyToken),
      Math.max(5_000, msUntilRenew),
    );

    // Tabs that slept past expiry: renew immediately on wake instead of
    // letting the first fetch burst discover the dead token.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const access = getStoredAccessToken();
      const p = access ? decodeJwtPayload(access) : null;
      if (!p || isExpired(p, 150)) {
        void refreshAccessToken().then(applyToken);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state]);

  const value: AuthContextValue = {
    user: state.phase === 'ready' ? state.user : null,
    getToken: getStoredAccessToken,
    refresh: refreshAccessToken,
    signOut: () => {
      clearTokens();
      redirectToShell();
    },
  };

  if (state.phase !== 'ready') {
    return createElement(
      'div',
      {
        className:
          'min-h-screen flex items-center justify-center bg-page text-muted text-sm',
      },
      state.phase === 'redirecting' ? 'Redirecting to Innu Shell…' : 'Authenticating…',
    );
  }

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
